import { validateUrl } from '@/lib/ssrf-validator';

export interface WebsiteEntryValidation {
  normalizedUrl: string;
  error: string | null;
}

/**
 * Validates and normalizes a website a user typed into a field.
 *
 * Extracted from `onboarding-v2/steps/BrandStep.tsx` (2026-08-22) because the
 * "Add a brand" dialog asked for the same website with a bare `<Input>` and no
 * validation at all — it handed whatever was typed straight to the Brand
 * researcher, so `htp://foo` or `my site` reached the scanner as a URL. Two
 * surfaces asking for the same thing must agree on what a valid answer is;
 * the rule cannot live inside one of the two screens.
 *
 * Accepts a bare host (`northbloom.com`), upgrades `http://` to `https://`,
 * and rejects anything the SSRF validator would refuse to fetch — private
 * ranges, reserved test TLDs, non-http schemes.
 */
export function validateWebsiteEntry(value: string): WebsiteEntryValidation {
  const trimmed = value.trim();
  if (!trimmed) return { normalizedUrl: '', error: 'Enter your website.' };
  if (/\s/.test(trimmed)) {
    return { normalizedUrl: '', error: 'Enter a website like example.com.' };
  }

  const schemeMatch = trimmed.match(/^([a-z][a-z\d+.-]*):\/\//i);
  if (schemeMatch && !/^https?:\/\//i.test(trimmed)) {
    return { normalizedUrl: '', error: 'Enter a website like example.com.' };
  }

  const candidate = /^http:\/\//i.test(trimmed)
    ? trimmed.replace(/^http:\/\//i, 'https://')
    : /^https:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
  const validation = validateUrl(candidate);
  if (validation.valid === false) {
    const isPublicAddressError =
      validation.reason.includes('blocked') ||
      validation.reason.includes('private/reserved address');
    return {
      normalizedUrl: '',
      error: isPublicAddressError ? 'Enter a public website.' : 'Enter a website like example.com.',
    };
  }

  const hostname = validation.parsed.hostname;
  const isIpv4 = /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname);
  const isIpv6 = hostname.includes(':');
  const isReservedTestHostname =
    hostname === 'localhost' || /\.(?:example|invalid|localhost|test)$/i.test(hostname);
  if (isReservedTestHostname) {
    return { normalizedUrl: '', error: 'Enter a public website.' };
  }
  if (!hostname.includes('.') && !isIpv4 && !isIpv6) {
    return { normalizedUrl: '', error: 'Enter a website like example.com.' };
  }

  const path = validation.parsed.pathname === '/' ? '' : validation.parsed.pathname;
  return {
    normalizedUrl: `${validation.parsed.origin}${path}${validation.parsed.search}`,
    error: null,
  };
}

export function normalizeWebsiteUrl(value: string): string {
  return validateWebsiteEntry(value).normalizedUrl;
}
